import { X } from 'lucide-react';

import { cn } from '@rocco/utils/cn';

export interface AppliedFilterPillItem {
    /** Stable identity for rendering. */
    id: string;
    label: string;
    onRemove: () => void;
}

interface AppliedFilterPillsProps {
    items: AppliedFilterPillItem[];
    onClearAll: () => void;
    /** `dark` renders light-on-dark pills for dark editorial surfaces. */
    theme?: 'light' | 'dark';
    className?: string;
}

/**
 * Applied-filter pills: one removable outline pill per applied filter plus a
 * Clear all text action. Presentational — pair it with an adapter that derives
 * the items from the host page's filter state (e.g. {@link AppliedFiltersRow}
 * for the rocco filter system). Renders nothing with no items.
 */
export const AppliedFilterPills = ({
    items,
    onClearAll,
    theme = 'light',
    className,
}: AppliedFilterPillsProps) => {
    if (items.length === 0) return null;

    const dark = theme === 'dark';

    return (
        <section
            aria-label="Selected filters"
            className={cn('flex flex-wrap items-center gap-2', className)}
        >
            {items.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    aria-label={`Remove filter ${item.label}`}
                    onClick={item.onRemove}
                    className={cn(
                        'flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border bg-transparent px-3 py-1.5 font-sans text-sm transition-colors',
                        dark
                            ? 'border-white/40 text-white hover:border-white'
                            : 'border-[#181818]/30 text-[#181818] hover:border-[#181818]',
                    )}
                >
                    {item.label}
                    <X aria-hidden className="size-3.5" />
                </button>
            ))}
            <button
                type="button"
                onClick={onClearAll}
                className={cn(
                    'cursor-pointer px-2 font-sans text-sm underline underline-offset-2 transition-colors',
                    dark
                        ? 'text-white/70 hover:text-white'
                        : 'text-gray-500 hover:text-black',
                )}
            >
                Clear all
            </button>
        </section>
    );
};
